import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Sparkles,
  Copy,
  Download,
  Save,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";

export default function CreateCampaignPage() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("brief"); // brief | processing | review
  const [brief, setBrief] = useState("");
  const [campaignData, setCampaignData] = useState(null);
  const [rawJson, setRawJson] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [udcData, setUdcData] = useState(null);
  const [mandatoryMissing, setMandatoryMissing] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    specifications: true,
    assets: false,
    questions: false,
    pacing: false,
    pricing: false,
  });
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/udc`, { headers }).then((r) => setUdcData(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const processBrief = async () => {
    if (!brief.trim()) {
      toast.error("Please enter a campaign brief");
      return;
    }
    setStep("processing");
    setError("");
    try {
      const res = await axios.post(
        `${API}/campaigns/process-brief`,
        { brief },
        { headers, timeout: 120000 }
      );
      if (res.data.campaign_data) {
        setCampaignData(res.data.campaign_data);
        setRawJson(JSON.stringify(res.data.campaign_data, null, 2));
        setMandatoryMissing(res.data.mandatory_missing || []);
        setStep("review");
        if (res.data.mandatory_missing && res.data.mandatory_missing.length > 0) {
          toast.warning(`${res.data.mandatory_missing.length} mandatory field(s) need attention`);
        } else {
          toast.success("Campaign data generated successfully");
        }
      } else {
        setError(res.data.error || "Failed to parse AI response");
        setRawJson(res.data.raw_output || "");
        setStep("review");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || "AI processing failed. The AI service may be temporarily unavailable — please try again in a moment.";
      setError(msg);
      setStep("brief");
      toast.error(msg);
    }
  };

  const MANDATORY_FIELDS = [
    "campaign_name", "type", "start_date", "end_date", "total_target", "total_budget",
    "delivery_days", "status", "specifications.employee_size", "specifications.industry_types",
    "specifications.revenue_size", "specifications.geo", "specifications.job_functions",
    "specifications.job_levels", "specifications.contacts_cap",
    "specifications.global_deduplication_window", "specifications.lead_types",
    "assets", "month_wise_pacing", "client_pricing", "remarks"
  ];

  const isMandatory = (path) => MANDATORY_FIELDS.includes(path);

  const getMandatoryMissing = (data) => {
    if (!data) return [];
    const missing = [];
    const topFields = ["campaign_name","type","start_date","end_date","total_target","total_budget","delivery_days","status","remarks"];
    topFields.forEach(f => { const v = data[f]; if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) missing.push(f); });
    const specs = data.specifications || {};
    const specFields = ["employee_size","industry_types","revenue_size","geo","job_functions","job_levels","contacts_cap","global_deduplication_window","lead_types"];
    specFields.forEach(f => { const v = specs[f]; if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) missing.push(`specifications.${f}`); });
    ["assets","month_wise_pacing","client_pricing"].forEach(f => { const v = data[f]; if (!v || !Array.isArray(v) || v.length === 0) missing.push(f); });
    return missing;
  };

  const saveCampaign = async () => {
    const dataToSave = campaignData || JSON.parse(rawJson);
    const missing = getMandatoryMissing(dataToSave);
    if (missing.length > 0) {
      toast.error(`Cannot save: ${missing.length} mandatory field(s) are empty — ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/campaigns`,
        { campaign_data: dataToSave, brief },
        { headers }
      );
      toast.success("Campaign saved successfully");
      navigate("/");
    } catch (err) {
      toast.error("Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const copyJson = async () => {
    const text = campaignData ? JSON.stringify(campaignData, null, 2) : rawJson;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("JSON copied to clipboard");
    } catch {
      // Fallback for environments where clipboard API is restricted
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success("JSON copied to clipboard");
    }
  };

  const downloadJson = () => {
    const text = campaignData ? JSON.stringify(campaignData, null, 2) : rawJson;
    const blob = new Blob([text], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${campaignData?.campaign_name || "campaign"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateField = (path, value) => {
    setCampaignData((prev) => {
      const data = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let ref = data;
      for (let i = 0; i < keys.length - 1; i++) {
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return data;
    });
  };

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const syntaxHighlight = (json) => {
    if (!json) return "";
    return json
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(
        /("(\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
          let cls = "json-number";
          if (/^"/.test(match)) {
            cls = /:$/.test(match) ? "json-key" : "json-string";
          } else if (/true|false/.test(match)) {
            cls = "json-boolean";
          } else if (/null/.test(match)) {
            cls = "json-null";
          }
          return `<span class="${cls}">${match}</span>`;
        }
      );
  };

  // Section component
  const Section = ({ title, sectionKey, children }) => (
    <div className="border border-border rounded-sm bg-card overflow-hidden">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors"
        data-testid={`section-toggle-${sectionKey}`}
      >
        <span className="text-xs text-muted-foreground uppercase tracking-[0.15em] font-medium">
          {title}
        </span>
        {expandedSections[sectionKey] ? (
          <ChevronUp size={14} className="text-muted-foreground" />
        ) : (
          <ChevronDown size={14} className="text-muted-foreground" />
        )}
      </button>
      {expandedSections[sectionKey] && (
        <div className="p-4 pt-0 border-t border-border/40">{children}</div>
      )}
    </div>
  );

  const FieldInput = ({ label, path, value, type = "text" }) => {
    const mandatory = isMandatory(path);
    const empty = value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
    return (
      <div>
        <label className="field-label">
          {label}{mandatory && <span className="text-destructive ml-0.5">*</span>}
        </label>
        <Input
          type={type}
          value={value || ""}
          onChange={(e) => updateField(path, e.target.value)}
          className={`bg-transparent text-foreground placeholder:text-muted-foreground h-10 px-4 rounded-sm text-sm ${mandatory && empty ? "border-destructive/50" : "border-border"}`}
          data-testid={`field-${path}`}
        />
      </div>
    );
  };

  const TagsField = ({ label, path, value, options }) => {
    const mandatory = isMandatory(path);
    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const items = Array.isArray(value) ? value : [];
    const empty = items.length === 0;
    const filtered = options
      ? options.filter(
          (o) =>
            !items.includes(o) &&
            o.toLowerCase().includes(search.toLowerCase())
        )
      : [];

    return (
      <div>
        <label className="field-label">
          {label}{mandatory && <span className="text-destructive ml-0.5">*</span>}
          {mandatory && empty && <span className="text-destructive text-[0.6rem] ml-2 normal-case tracking-normal">required</span>}
        </label>
        <div className="flex flex-wrap gap-1 mb-2">
          {items.map((item, i) => (
            <span key={i} className="tag-chip">
              {item}
              <button
                onClick={() =>
                  updateField(
                    path,
                    items.filter((_, j) => j !== i)
                  )
                }
                className="ml-1.5 text-muted-foreground hover:text-foreground"
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
        {options && (
          <div className="relative">
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="bg-transparent border-border text-foreground placeholder:text-muted-foreground h-9 px-3 rounded-sm text-xs"
              data-testid={`search-${path}`}
            />
            {isOpen && search && filtered.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-sm max-h-40 overflow-auto">
                {filtered.slice(0, 15).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => {
                      updateField(path, [...items, opt]);
                      setSearch("");
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // --- BRIEF STEP ---
  if (step === "brief") {
    return (
      <div data-testid="create-campaign-page">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={14} /> Back to Dashboard
        </button>

        <div className="max-w-3xl animate-in" style={{ animationDelay: "0.05s" }}>
          <p className="text-xs text-accent uppercase tracking-[0.2em] mb-2">
            AI Campaign Builder
          </p>
          <h1
            className="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight mb-2"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Describe Your Campaign
          </h1>
          <p className="text-sm text-muted-foreground mb-8 max-w-xl">
            Write a natural language brief about your campaign goals, target audience,
            industry, geography, budget, and any specifics. The AI will map it to
            aalign&apos;s campaign structure.
          </p>

          {error && (
            <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm p-4 rounded-sm mb-4">
              {error}
            </div>
          )}

          <Textarea
            data-testid="campaign-brief-input"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="Example: We need a content syndication campaign targeting IT Directors and VPs in the software development and IT services industries across the US, UK, and Germany. Employee size should be 1001-5000 and 5001-10000. Budget is $50,000 with 500 MQL leads. Campaign should run for Q1 2026 with delivery on weekdays. Focus on cybersecurity and cloud computing topics."
            className="bg-card border-border text-foreground placeholder:text-muted-foreground min-h-[200px] p-5 rounded-sm text-sm leading-relaxed resize-none"
            rows={10}
          />

          <div className="flex items-center gap-3 mt-6">
            <Button
              onClick={processBrief}
              disabled={!brief.trim()}
              data-testid="process-brief-btn"
              className="bg-accent hover:bg-accent/85 text-accent-foreground font-medium rounded-sm h-11 px-6 text-sm active:scale-95 transition-transform disabled:opacity-30"
            >
              <Sparkles size={16} className="mr-2" />
              Process with AI
            </Button>
            <span className="text-xs text-muted-foreground">
              Powered by aalign
            </span>
          </div>

          {/* Sample Briefs */}
          <div className="mt-10 border-t border-border pt-6">
            <p className="text-xs text-muted-foreground uppercase tracking-[0.15em] mb-3">
              Sample Briefs
            </p>
            <div className="space-y-2">
              {[
                "We need an Email Marketing campaign targeting C-level executives in Financial Services and Banking across EMEA. Budget $100K for 200 leads. Focus on digital transformation. Q2 2026.",
                "Email Marketing for HR Directors and VPs in IT Services and Software Development in the US and Canada. 500 MQLs, $50K budget, Q1 2026. Topics: employee engagement and talent acquisition.",
                "Tele-Marketing campaign targeting Marketing Managers in Healthcare and Pharmaceutical Manufacturing. 300 leads, $75K. Geo: United States, United Kingdom, Germany. 6-month duration.",
              ].map((sample, i) => (
                <button
                  key={i}
                  onClick={() => setBrief(sample)}
                  className="w-full text-left p-3 bg-card border border-border rounded-sm text-xs text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                  data-testid={`sample-brief-${i}`}
                >
                  {sample}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- PROCESSING STEP ---
  if (step === "processing") {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[60vh]"
        data-testid="processing-state"
      >
        <div className="ai-glow p-8 rounded-sm bg-card border border-accent/20 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="ai-dot dot-bounce-1" />
            <div className="ai-dot dot-bounce-2" />
            <div className="ai-dot dot-bounce-3" />
          </div>
          <p
            className="text-lg font-bold text-accent tracking-tight mb-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            AI is Processing Your Brief
          </p>
          <p className="text-xs text-muted-foreground">
            Mapping your requirements to aalign UDC values...
          </p>
        </div>
      </div>
    );
  }

  // --- REVIEW STEP ---
  return (
    <div data-testid="review-step">
      <button
        onClick={() => setStep("brief")}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-6 transition-colors"
        data-testid="back-to-brief"
      >
        <ArrowLeft size={14} /> Back to Brief
      </button>

      <div className="flex flex-col lg:flex-row gap-6 animate-in" style={{ animationDelay: "0.05s" }}>
        {/* Left: Edit Form */}
        <div className="flex-1 min-w-0 lg:max-w-[60%]">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs text-accent uppercase tracking-[0.2em] mb-1">
                AI Generated
              </p>
              <h1
                className="text-2xl font-extrabold text-foreground tracking-tight"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                Review Campaign
              </h1>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={saveCampaign}
                disabled={saving}
                data-testid="save-campaign-btn"
                className="bg-primary hover:bg-primary/85 text-primary-foreground rounded-sm h-9 px-4 text-sm active:scale-95 transition-transform"
              >
                <Save size={14} className="mr-1.5" />
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm p-4 rounded-sm mb-4">
              {error}
            </div>
          )}

          {mandatoryMissing.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-500 text-sm p-4 rounded-sm mb-4" data-testid="mandatory-warning">
              <span className="font-medium">Mandatory fields missing:</span>{" "}
              {mandatoryMissing.join(", ")}. Please fill them before saving.
            </div>
          )}

          {campaignData && (
            <div className="space-y-3">
              {/* General */}
              <Section title="General Information" sectionKey="general">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3">
                  <FieldInput
                    label="Campaign Name"
                    path="campaign_name"
                    value={campaignData.campaign_name}
                  />
                  <FieldInput
                    label="Type"
                    path="type"
                    value={campaignData.type}
                  />
                  <FieldInput
                    label="Start Date"
                    path="start_date"
                    value={campaignData.start_date}
                  />
                  <FieldInput
                    label="End Date"
                    path="end_date"
                    value={campaignData.end_date}
                  />
                  <FieldInput
                    label="Total Target"
                    path="total_target"
                    value={campaignData.total_target}
                  />
                  <FieldInput
                    label="Total Budget (USD)"
                    path="total_budget"
                    value={campaignData.total_budget}
                  />
                  <FieldInput
                    label="Status"
                    path="status"
                    value={campaignData.status}
                  />
                </div>
                <div className="mt-4">
                  <TagsField
                    label="Delivery Days"
                    path="delivery_days"
                    value={campaignData.delivery_days}
                    options={udcData?.delivery_days}
                  />
                </div>
              </Section>

              {/* Specifications */}
              <Section title="Specifications" sectionKey="specifications">
                <div className="space-y-4 pt-3">
                  <FieldInput
                    label="Job Title Keywords"
                    path="specifications.job_title_keywords"
                    value={campaignData.specifications?.job_title_keywords}
                  />
                  <TagsField
                    label="Employee Size"
                    path="specifications.employee_size"
                    value={campaignData.specifications?.employee_size}
                    options={udcData?.employee_sizes}
                  />
                  <TagsField
                    label="Industry Types"
                    path="specifications.industry_types"
                    value={campaignData.specifications?.industry_types}
                    options={udcData?.industry_types}
                  />
                  <TagsField
                    label="Revenue Size"
                    path="specifications.revenue_size"
                    value={campaignData.specifications?.revenue_size}
                    options={udcData?.revenue_sizes}
                  />
                  <TagsField
                    label="Geography"
                    path="specifications.geo"
                    value={campaignData.specifications?.geo}
                    options={udcData?.countries?.map((c) => c.name)}
                  />
                  <TagsField
                    label="Job Functions"
                    path="specifications.job_functions"
                    value={campaignData.specifications?.job_functions}
                    options={udcData?.job_functions}
                  />
                  <TagsField
                    label="Job Levels"
                    path="specifications.job_levels"
                    value={campaignData.specifications?.job_levels}
                    options={udcData?.job_levels}
                  />
                  <TagsField
                    label="Lead Types"
                    path="specifications.lead_types"
                    value={campaignData.specifications?.lead_types}
                    options={udcData?.lead_types}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FieldInput
                      label="Contacts Cap"
                      path="specifications.contacts_cap"
                      value={campaignData.specifications?.contacts_cap}
                      type="number"
                    />
                    <FieldInput
                      label="Dedup Window"
                      path="specifications.global_deduplication_window"
                      value={campaignData.specifications?.global_deduplication_window}
                    />
                  </div>
                </div>
              </Section>

              {/* Assets */}
              <Section title="Assets" sectionKey="assets">
                <div className="space-y-3 pt-3">
                  {(campaignData.assets || []).map((asset, i) => (
                    <div key={i} className="grid grid-cols-3 gap-2">
                      <FieldInput
                        label="Asset Name"
                        path={`assets.${i}.asset_name`}
                        value={asset.asset_name}
                      />
                      <FieldInput
                        label="Asset Type"
                        path={`assets.${i}.asset_type`}
                        value={asset.asset_type}
                      />
                      <FieldInput
                        label="Remarks"
                        path={`assets.${i}.remarks`}
                        value={asset.remarks}
                      />
                    </div>
                  ))}
                </div>
              </Section>

              {/* Custom Questions */}
              <Section title="Custom Questions" sectionKey="questions">
                <div className="space-y-3 pt-3">
                  {(campaignData.custom_questions || []).map((q, i) => (
                    <div key={i} className="bg-secondary border border-border p-3 rounded-sm">
                      <FieldInput
                        label={`Question ${i + 1}`}
                        path={`custom_questions.${i}.question`}
                        value={q.question}
                      />
                      <div className="mt-2">
                        <TagsField
                          label="Answers"
                          path={`custom_questions.${i}.answers`}
                          value={q.answers}
                          options={null}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Pacing */}
              <Section title="Month-wise Pacing" sectionKey="pacing">
                <div className="space-y-2 pt-3">
                  {(campaignData.month_wise_pacing || []).map((p, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2">
                      <FieldInput
                        label="Month"
                        path={`month_wise_pacing.${i}.month`}
                        value={p.month}
                      />
                      <FieldInput
                        label="Pacing"
                        path={`month_wise_pacing.${i}.pacing`}
                        value={p.pacing}
                        type="number"
                      />
                    </div>
                  ))}
                </div>
              </Section>

              {/* Pricing */}
              <Section title="Client Pricing" sectionKey="pricing">
                <div className="space-y-2 pt-3">
                  {(campaignData.client_pricing || []).map((p, i) => (
                    <div key={i} className="grid grid-cols-3 gap-2">
                      <FieldInput
                        label="Lead Type"
                        path={`client_pricing.${i}.lead_type`}
                        value={p.lead_type}
                      />
                      <FieldInput
                        label="Price"
                        path={`client_pricing.${i}.price`}
                        value={p.price}
                      />
                      <FieldInput
                        label="Target"
                        path={`client_pricing.${i}.target`}
                        value={p.target}
                        type="number"
                      />
                    </div>
                  ))}
                </div>
              </Section>

              {/* Remarks */}
              <div className="border border-border rounded-sm bg-card p-4">
                <label className="field-label">Remarks</label>
                <Textarea
                  value={campaignData.remarks || ""}
                  onChange={(e) => updateField("remarks", e.target.value)}
                  className="bg-transparent border-border text-foreground text-sm rounded-sm min-h-[60px] mt-1"
                  data-testid="field-remarks"
                />
              </div>
            </div>
          )}
        </div>

        {/* Right: JSON Viewer */}
        <div className="lg:w-[40%] lg:sticky lg:top-20 lg:self-start">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-[0.15em]">
              JSON Output
            </p>
            <div className="flex gap-1.5">
              <button
                onClick={copyJson}
                data-testid="copy-json-btn"
                className="p-1.5 text-muted-foreground hover:text-foreground border border-border rounded-sm transition-colors"
                title="Copy JSON"
              >
                <Copy size={14} />
              </button>
              <button
                onClick={downloadJson}
                data-testid="download-json-btn"
                className="p-1.5 text-muted-foreground hover:text-foreground border border-border rounded-sm transition-colors"
                title="Download JSON"
              >
                <Download size={14} />
              </button>
            </div>
          </div>
          <div
            className="json-viewer max-h-[75vh] overflow-auto"
            data-testid="json-output-viewer"
            dangerouslySetInnerHTML={{
              __html: syntaxHighlight(
                campaignData
                  ? JSON.stringify(campaignData, null, 2)
                  : rawJson
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}
